import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const textareaVariants = cva(
  "flex w-full resize-none overflow-y-auto text-foreground placeholder:text-input-placeholder focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 box-border transition-colors",
  {
    variants: {
      variant: {
        default:
          "min-h-12 max-h-36 rounded-lg border border-input-border bg-input-bg px-3 py-2.5 ring-offset-background hover:bg-secondary-dark focus-visible:bg-input-bg-focus focus-visible:border-input-border-focus focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0",
        card: "min-h-12 max-h-36 border-none bg-transparent p-0 shadow-none leading-relaxed hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:border-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface TextareaProps
  extends
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, value, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    React.useImperativeHandle(ref, () => textareaRef.current!);

    React.useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const adjustHeight = () => {
        textarea.style.height = "";
        textarea.style.height = `${textarea.scrollHeight}px`;
      };

      adjustHeight();
      textarea.addEventListener("input", adjustHeight);

      return () => textarea.removeEventListener("input", adjustHeight);
    }, [value]);

    return (
      <textarea
        className={cn(textareaVariants({ variant, className }))}
        ref={textareaRef}
        value={value}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
